// How a manifest-declared lens op reads as one row.
//
// Pure, and deliberately outside the feed component. Every hand-wired domain has a
// case in TraceWorkFeed's two switches; a manifest-declared one falls to the default,
// and both defaults were wrong at once — the title read the literal word "tool"
// because manifest ops carry no `name`, and the body returned null so the row drew no
// chevron and could not be opened. Neither was caught, because no fixture used a
// domain outside the named cases and the only way to reach this logic was to import
// the whole component tree.
//
// It is not presentational: it decides what a reader is told an op was. Keeping it
// here makes it testable in milliseconds rather than by mounting a feed.

// Two different things share the `checks` key: built-in lens checks are
// structured probes ({kind, tool, outcome}), runtime/artifact claims are
// boolean verdicts ({name, ok}). The shape test is the boolean.
const isClaim = (c) => c && typeof c.ok === "boolean";

/** The one-line claim: what the act was, and what it found. */
export function manifestRowTitle(op = {}) {
  // Runtime lenses and behaviour artifacts carry ordered field rows; the first
  // one is the identity the lens groups by, which is the best one-line handle
  // a generic row has. Claims summarize as their verdict marks — a ✗ in the
  // meta is the falsifier firing where a scanning reader actually looks.
  if (op.fields?.length) {
    const claims = (op.checks || []).filter(isClaim);
    return {
      title: String(op.fields[0].value ?? op.name ?? "op"),
      meta: claims.length
        ? claims.map((c) => `${c.ok ? "✓" : "✗"} ${c.name}`).join(" · ")
        : op.outcome || null,
    };
  }
  const check = op.checks?.[0];
  if (check && !isClaim(check)) {
    return {
      title: [check.kind, check.tool].filter(Boolean).join(" "),
      meta: [check.outcome, ...(check.scope || [])].filter(Boolean).join(" · ") || null,
    };
  }
  const step = op.steps?.[0];
  if (step) return { title: step.kind || "ran", meta: step.cmd || null };
  return { title: op.name || "tool", meta: null };
}

/**
 * The rows behind the claim, as data. Null means there is nothing to expand, which is
 * what suppresses the chevron.
 */
export function manifestRowBody(op = {}) {
  const sections = op.sections || [];
  // For a batched call `steps` and `sections` derive from the same data, so rendering
  // both listed every asked question twice.
  const steps = sections.length ? [] : (op.steps || []).filter((x) => x && x.cmd);
  const claims = (op.checks || []).filter(isClaim);
  const checks = (op.checks || []).filter((c) => !isClaim(c));
  // Field rows past the first (the title already carries it), plus the output
  // when the op kept one — the evidence a reader falls back to.
  const fields = (op.fields || []).slice(1).map((f) => ({ label: f.name, text: String(f.value ?? "") }));
  if (op.output) fields.push({ label: "output", text: String(op.output) });
  if (!steps.length && !checks.length && !sections.length && !claims.length && !fields.length) return null;
  return {
    steps: [
      ...steps.map((s) => ({ label: s.kind, text: s.cmd })),
      ...fields,
    ],
    // A question the system had nothing to say to is a different fact from one that
    // was never asked.
    sections: sections.map((s) => ({ label: s.empty ? "no answer" : `${s.lines} lines`, text: s.marker })),
    checks: [
      ...checks.map((c) => ({
        label: c.kind,
        text: `${c.tool}${(c.scope || []).length ? ` · ${c.scope.join(" · ")}` : ""}`,
        outcome: c.outcome,
      })),
      ...claims.map((c) => ({ label: c.name, text: c.ok ? "held" : "did not hold", outcome: c.ok ? "✓" : "✗" })),
    ],
  };
}
